import React, { useState, useEffect } from 'react';
import styles from '../styles';

const SectionTitle = ({ children }) => (
  <h2
    style={{
      fontFamily: "'Antonio', sans-serif",
      fontSize: '28px',
      color: '#1B2B3E',
      textTransform: 'uppercase',
      letterSpacing: '-0.5px',
      marginTop: '64px',
      marginBottom: '16px',
    }}
  >
    {children}
  </h2>
);

const P = ({ children }) => (
  <p style={{ fontSize: '16px', color: '#475569', lineHeight: '1.8', marginBottom: '16px' }}>
    {children}
  </p>
);

const PrivacyPage = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div>
      <header
        style={{
          paddingTop: '120px',
          paddingBottom: '64px',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(20px)',
          transition:
            'opacity 1s cubic-bezier(0.2, 0.8, 0.2, 1), transform 1s cubic-bezier(0.2, 0.8, 0.2, 1)',
        }}
      >
        <div style={styles.container}>
          <h1 style={styles.heroTitleUpper}>Privacy<br />Policy.</h1>
          <p style={styles.heroSub}>
            Your privacy matters. This policy explains how FCR Solutions collects, uses, and
            protects your personal information.
          </p>
        </div>
      </header>

      <section style={{ ...styles.container, paddingBottom: '120px', maxWidth: '800px' }}>
        <P>
          <strong>Effective Date:</strong> January 1, 2026
        </P>
        <P>
          FCR Solutions ("we," "our," or "us") is committed to protecting the privacy and security
          of your personal information. This Privacy Policy describes how we collect, use, disclose,
          and safeguard your information when you visit our website or use our credit repair services.
        </P>

        <SectionTitle>1. Information We Collect</SectionTitle>
        <P>
          We may collect personal information that you voluntarily provide when you use our services,
          including but not limited to:
        </P>
        <ul style={{ fontSize: '16px', color: '#475569', lineHeight: '2', marginBottom: '16px', paddingLeft: '24px' }}>
          <li>Full name, email address, phone number, and mailing address</li>
          <li>Social Security Number (encrypted and stored securely)</li>
          <li>Date of birth</li>
          <li>Credit report data and financial information</li>
          <li>Government-issued identification for identity verification</li>
          <li>Payment and billing information</li>
        </ul>
        <P>
          We may also automatically collect certain information when you visit our website, including
          your IP address, browser type, operating system, referring URLs, and browsing behavior
          through cookies and similar technologies.
        </P>

        <SectionTitle>2. How We Use Your Information</SectionTitle>
        <P>We use the information we collect to:</P>
        <ul style={{ fontSize: '16px', color: '#475569', lineHeight: '2', marginBottom: '16px', paddingLeft: '24px' }}>
          <li>Provide, operate, and maintain our credit repair services</li>
          <li>Process disputes and communications with credit bureaus on your behalf</li>
          <li>Communicate with you regarding your account, updates, and service progress</li>
          <li>Process payments and manage billing</li>
          <li>Comply with legal obligations under the Credit Repair Organizations Act (CROA)</li>
          <li>Improve our website and services through analytics</li>
          <li>Send promotional communications (with your consent)</li>
        </ul>

        <SectionTitle>3. How We Protect Your Information</SectionTitle>
        <P>
          We implement industry-standard security measures to protect your personal information,
          including 256-bit SSL/TLS encryption for data in transit, AES-256 encryption for data at
          rest, and strict access controls. Our systems are regularly audited and monitored for
          vulnerabilities. However, no method of electronic transmission or storage is 100% secure,
          and we cannot guarantee absolute security.
        </P>

        <SectionTitle>4. Information Sharing and Disclosure</SectionTitle>
        <P>
          We do not sell, trade, or rent your personal information to third parties. We may share
          your information only in the following circumstances:
        </P>
        <ul style={{ fontSize: '16px', color: '#475569', lineHeight: '2', marginBottom: '16px', paddingLeft: '24px' }}>
          <li><strong>Credit Bureaus:</strong> To process disputes and corrections on your behalf (Equifax, Experian, TransUnion)</li>
          <li><strong>Service Providers:</strong> Trusted third parties who assist in operating our business (payment processors, secure hosting providers)</li>
          <li><strong>Legal Requirements:</strong> When required by law, subpoena, or government request</li>
          <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
        </ul>

        <SectionTitle>5. Cookies and Tracking Technologies</SectionTitle>
        <P>
          Our website uses cookies and similar tracking technologies to enhance your browsing
          experience, analyze site traffic, and understand where our visitors come from. You can
          control cookie preferences through your browser settings. Disabling cookies may limit
          certain features of our website.
        </P>

        <SectionTitle>6. Your Rights and Choices</SectionTitle>
        <P>You have the right to:</P>
        <ul style={{ fontSize: '16px', color: '#475569', lineHeight: '2', marginBottom: '16px', paddingLeft: '24px' }}>
          <li>Access, correct, or delete your personal information</li>
          <li>Opt out of promotional communications at any time</li>
          <li>Request a copy of the data we hold about you</li>
          <li>Withdraw consent for data processing where applicable</li>
          <li>File a complaint with a regulatory authority</li>
        </ul>
        <P>
          California residents have additional rights under the CCPA, including the right to know
          what personal information is collected and the right to request deletion.
        </P>

        <SectionTitle>7. Data Retention</SectionTitle>
        <P>
          We retain your personal information for as long as necessary to fulfill the purposes for
          which it was collected, comply with legal obligations, resolve disputes, and enforce our
          agreements. Credit-related records are retained for a minimum of three years following
          service completion as required by federal regulations.
        </P>

        <SectionTitle>8. Third-Party Links</SectionTitle>
        <P>
          Our website may contain links to third-party websites. We are not responsible for the
          privacy practices or content of these sites. We encourage you to review the privacy
          policies of any third-party sites you visit.
        </P>

        <SectionTitle>9. Children's Privacy</SectionTitle>
        <P>
          Our services are not intended for individuals under the age of 18. We do not knowingly
          collect personal information from children. If we become aware that we have collected
          information from a minor, we will take steps to delete it promptly.
        </P>

        <SectionTitle>10. Changes to This Policy</SectionTitle>
        <P>
          We may update this Privacy Policy from time to time. We will notify you of any material
          changes by posting the updated policy on our website with a revised effective date. Your
          continued use of our services after changes are posted constitutes acceptance of the
          updated policy.
        </P>

        <SectionTitle>11. Contact Us</SectionTitle>
        <P>
          If you have questions or concerns about this Privacy Policy or our data practices, please
          contact us:
        </P>
        <P>
          FCR Solutions<br />
          4193 Flat Rock Dr, Bldg 200<br />
          Riverside, CA 92505<br />
          Email: fcrcreditsolutions@gmail.com<br />
          Phone: 888.271.2293
        </P>
      </section>
    </div>
  );
};

export default PrivacyPage;
